import { useContext, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKey } from 'src/query-client';
import Table, { ColumnsType } from 'antd/lib/table';
import { PjmAddressDto } from 'src/domain/pjm/dto/PjmAddress';
import { PjmRegistrationDto } from 'src/domain/pjm/dto/PjmRegistrationDto';
import { AbilityContext } from 'src/components/ability/can';
import { IAction, ActionList } from 'src/components/table/actionList/actionList';
import { PjmRegistrationLocationEditModal } from './PjmRegistrationLocationEditModal';
import { PjmRegistrationStatus } from 'src/domain/pjm/dto/enums';
import { CustomizableColumnType } from 'src/components/table/CustomizableTable';

interface IRegistrationLocationTableProps {
    value: PjmRegistrationDto;
    onLocationChange?: (isUpdated: boolean) => void;
}

export function RegistrationLocationTable({ value, onLocationChange }: IRegistrationLocationTableProps) {
    const ability = useContext(AbilityContext);
    const [editingLocation, setEditingLocation] = useState<PjmRegistrationDto | null>(null);
    const queryClient = useQueryClient();

    const data = value ? [value] : [];

    const columns: ColumnsType<PjmRegistrationDto> = [
        {
            title: 'Location ID',
            dataIndex: ['location', 'id'],
            key: 'id',
            fixed: 'left',
            minWidth: 120,
        },
        {
            title: 'Location',
            dataIndex: ['location', 'summaryInfo', 'name'],
            key: 'name',
            minWidth: 200,
        },
        {
            title: 'Status',
            dataIndex: ['location', 'summaryInfo', 'status'],
            key: 'status',
        },
        {
            title: 'Zone',
            dataIndex: ['location', 'summaryInfo', 'zone'],
            key: 'zone',
        },
        {
            title: 'EDC Account Number',
            dataIndex: ['location', 'summaryInfo', 'EDC', 'accountNo'],
            key: 'accountNo',
            minWidth: 180,
        },
        {
            title: 'Pricing Point',
            dataIndex: ['locationInfo', 'pricingPoint'],
            key: 'pricingPoint',
        },
        {
            title: 'Address',
            dataIndex: ['siteInfo', 'address'],
            key: 'address',
            minWidth: 250,
            render: (text, record) => formatAddress(record.location?.siteInfo?.address),
        },
        {
            title: 'Capacity Loss Factor',
            dataIndex: ['locationInfo', 'capacityLossFactor'],
            key: 'capacityLossFactor',
            minWidth: 140,
        },
        {
            title: 'Energy Loss Factor',
            dataIndex: ['locationInfo', 'energyLossFactor'],
            key: 'energyLossFactor',
            minWidth: 140,
        },
        {
            title: 'Peak Load Contribution (kW)',
            dataIndex: ['locationInfo', 'peakLoadContribution'],
            key: 'peakLoadContribution',
            minWidth: 150,
        },
        {
            title: 'Winter Peak Load (kW)',
            dataIndex: ['locationInfo', 'winterPeakLoad'],
            key: 'winterPeakLoad',
            minWidth: 140,
        },
        {
            title: 'DY',
            dataIndex: ['locationInfo', 'dy'],
            key: 'dy',
        },
        {
            title: 'Winter Peak Load Report',
            // dataIndex: ['locationSummaryInfo', 'winterPeakLoad'],
            key: 'winterPeakLoadReport',
            minWidth: 140,
        },
        {
            title: 'Max Load Reduction (kW)',
            dataIndex: ['locationSummaryInfo', 'maximumLoadReductionCapability'],
            key: 'maximumLoadReductionCapability',
            minWidth: 140,
        },
        {
            title: 'Meter Type',
            dataIndex: ['locationInfo', 'meterType'],
            key: 'meterType',
            render: (text, record) => `${record.locationInfo?.meterType || ''}`,
        },
        {
            title: 'RERRA',
            dataIndex: 'rerra',
            key: 'rerra',
        },
        ...(ability.can('update', 'PjmLocation') && value.summaryInfo.status === PjmRegistrationStatus.NEW && value.uuid
            ? [
                  {
                      key: 'action',
                      fixed: 'right',
                      minWidth: 60,
                      render: (_: unknown, record: PjmRegistrationDto) => {
                          const actions: IAction[] = [
                              {
                                  title: 'Edit',
                                  onClick: (item: PjmRegistrationDto) => () => setEditingLocation(item),
                              },
                          ];
                          return <ActionList actions={actions} item={record} />;
                      },
                  } as CustomizableColumnType<PjmRegistrationDto>,
              ]
            : []),
    ];

    function onClose(saved?: boolean) {
        setEditingLocation(null);
        if (saved) {
            queryClient.invalidateQueries([QueryKey.PJM_LOCATIONS]);
            queryClient.invalidateQueries([QueryKey.PJM_REGISTRATIONS]);
            onLocationChange?.(true);
        }
    }

    return (
        <>
            <Table dataSource={data} columns={columns} pagination={false} rowKey="locationUuid" tableLayout={'auto'} scroll={{ x: 'scroll' }} />
            {editingLocation && <PjmRegistrationLocationEditModal value={editingLocation} onClose={onClose} />}
        </>
    );
}

function formatAddress(address?: PjmAddressDto) {
    if (!address) return '';

    const fullAddressList = [];
    if (address.address1) {
        fullAddressList.push(address.address1);
    }
    if (address.address2) {
        fullAddressList.push(address.address2);
    }
    if (address.city) {
        fullAddressList.push(address.city);
    }
    if (address.state) {
        fullAddressList.push(address.state);
    }
    if (address.zipCode) {
        fullAddressList.push(address.zipCode);
    }
    return fullAddressList.join(', ');
}
